import { useApolloClient } from '@apollo/client';
import { useCallback, useContext, useState } from 'react';
import { Box, MediaObject } from '@zillow/constellation-10';
import {
  ChatAvatar,
  ChatBubble,
  isMessageStatusVariant,
  MESSAGE_STATUS,
  MessageFooter,
  type MessageStatusVariant,
} from '@zg-rentals/satellite-components';
import {
  ConversationList_GetConversationsDocument,
  ConversationList_GetConversationsQuery,
  ConversationList_GetConversationsQueryVariables,
  MessageList_GetConversationDocument,
  MessageList_GetConversationQuery,
  MessageList_GetConversationQueryVariables,
  useAddMessage_AddMessageToConversationV2Mutation,
} from '../../../../types/GraphqlTypes';
import useFeatureFlagsHelper from '../../../../hooks/useFeatureFlagsHelper';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import stringUtils from '../../../../utils/stringUtils';
import { MAX_OLD_CONVERSATION_DAYS } from '../../../../constants/ConfigConstants';
import { ConversationListConversation } from '../../../ConversationList';
import MessageAttachments, { AttachmentsForMessage } from '../../../MessageAttachments';
import type { HandleOpenDeleteAttachmentsModal, HandleOpenImagesViewer } from '../../../MessageList';
import { logApolloError } from '../../../../utils/errorUtils';
import ChatBubbleWithActions from '../../../ChatBubbleWithActions';

type Props = {
  attachments: AttachmentsForMessage;
  isMessageOwner: boolean;
  messageContent: string;
  messageDateMs: string;
  senderName: string;
  shouldDisplaySentStatus: boolean;
  uiTreatmentType: string;
  messageId: string;
  openDeleteAttachmentsModal: HandleOpenDeleteAttachmentsModal;
  openImagesViewer: HandleOpenImagesViewer;
};

const getAttachmentZDocIdsForAddMessageV2Mutation = (attachments: AttachmentsForMessage): Array<string> => {
  return attachments.map(({ zDocId }) => zDocId || '');
};

const ListingMessage = ({
  attachments,
  isMessageOwner,
  messageContent,
  messageDateMs,
  senderName,
  shouldDisplaySentStatus,
  messageId,
  uiTreatmentType,
  openDeleteAttachmentsModal,
  openImagesViewer,
}: Props) => {
  const apolloClient = useApolloClient();
  const { enableMessageToolbar } = useFeatureFlagsHelper();
  const {
    conversationId,
    daysBack,
    listingAlias,
    isByProperty,
    clientTimeZone,
    folder,
    renterProgressStatus,
    readRepliedStatus,
  } = useContext(InboxPageContext);

  const [addMessageMutation] = useAddMessage_AddMessageToConversationV2Mutation();
  const statusFromUiTreatment = uiTreatmentType.split('-').length > 1 ? uiTreatmentType.split('-')[1] : undefined;
  const validStatus = isMessageStatusVariant(statusFromUiTreatment) ? statusFromUiTreatment : MESSAGE_STATUS.SENT;
  const [sendStatus, setSendStatus] = useState<MessageStatusVariant | undefined>(validStatus);
  const hasAttachments = attachments && !!attachments.length && Object.keys(attachments[0]).length !== 0;
  const isMessageContentEmpty = stringUtils.isEmptyString(messageContent);
  const chatBubbleVariant = isMessageOwner ? 'messageOwner' : 'default';
  const shouldAdjustOpacity = sendStatus === MESSAGE_STATUS.SENDING || sendStatus === MESSAGE_STATUS.FAILED;

  const handleResendMessage = useCallback(() => {
    setSendStatus(MESSAGE_STATUS.SENDING);
    const zDocIds = hasAttachments ? getAttachmentZDocIdsForAddMessageV2Mutation(attachments) : [];
    addMessageMutation({
      variables: {
        conversationId: conversationId!,
        listingAlias: listingAlias!,
        message: messageContent,
        zDocIds,
      },
      onError(error) {
        logApolloError(error, {
          details: 'ListingMessage - Error resending failed message item.',
          conversationId,
          listingAlias,
          messageContent,
        });
        setSendStatus(MESSAGE_STATUS.FAILED);
      },
      onCompleted(data) {
        const newMessage = data?.ZRM_addMessageToConversationV2;
        const messageListData = apolloClient.cache.readQuery<
          MessageList_GetConversationQuery,
          MessageList_GetConversationQueryVariables
        >({
          query: MessageList_GetConversationDocument,
          variables: {
            conversationId: conversationId!,
            listingAlias: listingAlias!,
            clientTimeZone,
          },
        });

        const currentMessages = messageListData?.ZRM_getConversationById.conversation || [];
        const tempMessages: MessageList_GetConversationQuery['ZRM_getConversationById']['conversation'] = [];
        const sentMessages = currentMessages.filter((message) => {
          const isTemp = message.messageId?.includes('temp');

          if (isTemp) {
            tempMessages.push(message);
          }
          return !isTemp;
        });

        const newConversation = [...sentMessages, newMessage, ...tempMessages];
        apolloClient.cache.writeQuery({
          query: MessageList_GetConversationDocument,
          variables: {
            conversationId: conversationId!,
            listingAlias: listingAlias!,
          },
          data: {
            ZRM_getConversationById: {
              ...messageListData?.ZRM_getConversationById,
              conversation: newConversation,
            },
          },
        });

        // Remove this failed message item from the cache
        const id = apolloClient.cache.identify({ __typename: 'ZRM_Message', conversationId, messageId });

        apolloClient.cache.evict({ id });
        apolloClient.cache.gc();

        const conversationListData = apolloClient.cache.readQuery<
          ConversationList_GetConversationsQuery,
          ConversationList_GetConversationsQueryVariables
        >({
          query: ConversationList_GetConversationsDocument,
          variables: {
            listingAlias: isByProperty ? listingAlias : null,
            clientTimeZone,
            daysBack: daysBack ? MAX_OLD_CONVERSATION_DAYS : null,
            folder,
            readRepliedStatus,
            renterProgressStatus,
          },
        });
        let currentConversation: ConversationListConversation | undefined;
        const updatedConversations =
          conversationListData?.ZRM_getLatestConversationsV2.conversations.filter((conversation) => {
            if (conversation.conversationId === conversationId) {
              currentConversation = conversation;
            }
            return conversation.conversationId != conversationId;
          }) || [];

        apolloClient.cache.writeQuery({
          query: ConversationList_GetConversationsDocument,
          variables: {
            listingAlias: isByProperty ? listingAlias : null,
            clientTimeZone,
            daysBack: daysBack ? MAX_OLD_CONVERSATION_DAYS : null,
            folder,
            readRepliedStatus,
            renterProgressStatus,
            // Custom flag used in ZRM_getLatestConversationsV2 apollo cache merge function
            skipMerge: true,
          },
          data: {
            ZRM_getLatestConversationsV2: {
              ...conversationListData?.ZRM_getLatestConversationsV2,
              conversations: [currentConversation, ...updatedConversations],
            },
          },
        });
      },
    });
  }, [conversationId, listingAlias, messageContent]);

  return (
    <MediaObject
      media={!isMessageOwner && <ChatAvatar senderFullName={senderName} type="user" />}
      css={{
        alignItems: 'end',
        display: 'flex',
        flexDirection: isMessageOwner ? 'row-reverse' : 'row',
        gap: 0,
        whiteSpace: 'pre-wrap',
        width: '100%',
      }}
    >
      <Box css={{ opacity: shouldAdjustOpacity ? 0.4 : 1 }}>
        {!isMessageContentEmpty && (
          <>
            {enableMessageToolbar ? (
              <ChatBubbleWithActions
                senderName={senderName}
                variant={chatBubbleVariant}
                messageContent={messageContent}
                isMessageOwner={isMessageOwner}
              />
            ) : (
              <ChatBubble senderName={senderName} variant={chatBubbleVariant}>
                {messageContent}
              </ChatBubble>
            )}
          </>
        )}

        {hasAttachments && (
          <MessageAttachments
            attachments={attachments}
            isMessageOwner={isMessageOwner}
            isMessageSent={sendStatus === MESSAGE_STATUS.SENT}
            messageId={messageId}
            openDeleteAttachmentsModal={openDeleteAttachmentsModal}
            openImagesViewer={openImagesViewer}
          />
        )}
      </Box>

      <MessageFooter
        isMessageOwner={isMessageOwner}
        messageDateMs={messageDateMs}
        resendMessage={handleResendMessage}
        sendStatus={sendStatus}
        shouldDisplaySentStatus={shouldDisplaySentStatus}
      />
    </MediaObject>
  );
};

export default ListingMessage;
